<script>
  import { ConvexClient } from "convex/browser";
  import { onMount } from "svelte";
  
  // Svelte 5 runes for reactive state
  let greeting = $state("");
  let users = $state([]);
  let userName = $state("");
  let userEmail = $state("");
  let loading = $state(false);
  let error = $state("");

  // Initialize Convex client with self-hosted URL
  const convex = new ConvexClient(import.meta.env.VITE_CONVEX_URL);

  onMount(async () => {
    await loadData();
  });

  async function loadData() {
    try {
      loading = true;
      error = "";
      
      // Test the sayHello query
      const helloResult = await convex.query("hello:sayHello");
      greeting = helloResult;
      
      // Load existing users
      const usersResult = await convex.query("users:getUsers");
      users = usersResult;
    } catch (err) {
      error = `Failed to load data: ${err.message}`;
      console.error("Error loading data:", err);
    } finally {
      loading = false;
    }
  }

  async function createUser() {
    if (!userName.trim() || !userEmail.trim()) {
      error = "Please fill in both name and email";
      return;
    }

    try {
      loading = true;
      error = "";
      
      await convex.mutation("users:createUser", {
        name: userName.trim(),
        email: userEmail.trim()
      });
      
      // Clear form and reload data
      userName = "";
      userEmail = "";
      await loadData();
    } catch (err) {
      error = `Failed to create user: ${err.message}`;
      console.error("Error creating user:", err);
    } finally {
      loading = false;
    }
  }
</script>

<main>
  <h1>=� Convex Self-Hosted Frontend</h1>
  
  {#if loading}
    <p class="loading">Loading...</p>
  {/if}
  
  {#if error}
    <p class="error">{error}</p>
  {/if}
  
  <section class="greeting">
    <h2>Server Status</h2>
    <p class="greeting-text">{greeting || "Connecting..."}</p>
  </section>
  
  <section class="user-form">
    <h2>Create User</h2>
    <form onsubmit={(e) => { e.preventDefault(); createUser(); }}>
      <input 
        bind:value={userName} 
        placeholder="Name" 
        type="text" 
        required 
        disabled={loading}
      />
      <input 
        bind:value={userEmail} 
        placeholder="Email" 
        type="email" 
        required 
        disabled={loading}
      />
      <button type="submit" disabled={loading}>
        {loading ? "Creating..." : "Create User"}
      </button>
    </form>
  </section>
  
  <section class="users-list">
    <h2>Users ({users.length})</h2>
    {#if users.length === 0}
      <p class="empty">No users found. Create one above! =F</p>
    {:else}
      <ul>
        {#each users as user (user._id)}
          <li>
            <strong>{user.name}</strong>
            <span class="email">{user.email}</span>
            <small>Created: {new Date(user.createdAt).toLocaleDateString()}</small>
          </li>
        {/each}
      </ul>
    {/if}
  </section>
  
  <button onclick={loadData} disabled={loading} class="refresh">
    {loading ? "Loading..." : "= Refresh"}
  </button>
</main>

<style>
  main {
    max-width: 800px;
    margin: 0 auto;
    padding: 2rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
  
  h1 {
    color: #ff3e00;
    text-align: center;
    margin-bottom: 2rem;
  }
  
  section {
    margin-bottom: 2rem;
    padding: 1.5rem;
    border: 1px solid #eee;
    border-radius: 8px;
    background: #fafafa;
  }
  
  .greeting-text {
    font-size: 1.2rem;
    color: #333;
    font-weight: bold;
  }
  
  .loading {
    color: #666;
    font-style: italic;
  }
  
  .error {
    color: #e63946;
    background: #ffebee;
    padding: 0.5rem;
    border-radius: 4px;
    border: 1px solid #ffcdd2;
  }
  
  .user-form form {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
  }
  
  .user-form input {
    flex: 1;
    min-width: 200px;
    padding: 0.5rem;
    border: 1px solid #ccc;
    border-radius: 4px;
  }
  
  .user-form button {
    padding: 0.5rem 1rem;
    background: #ff3e00;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
  }
  
  .user-form button:hover:not(:disabled) {
    background: #e63900;
  }
  
  .user-form button:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
  
  .users-list ul {
    list-style: none;
    padding: 0;
  }
  
  .users-list li {
    padding: 1rem;
    margin: 0.5rem 0;
    background: white;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  .email {
    color: #666;
    font-style: italic;
  }
  
  .empty {
    text-align: center;
    color: #666;
    font-style: italic;
  }
  
  .refresh {
    display: block;
    margin: 2rem auto 0;
    padding: 0.75rem 1.5rem;
    background: #28a745;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 1rem;
  }
  
  .refresh:hover:not(:disabled) {
    background: #218838;
  }
  
  .refresh:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
</style>